import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  COLORS,
  FONTS,
  Panel,
  SceneTransition,
  SlideTimeBar,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, NOTYOU, STUCK, ZOMBIE, DEADZONE, FIX, LANDMINE, LAYERS, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 92 }) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/** In-flow code card (traffic-light chrome + mono lines). Kept in normal flow so it
 *  stacks cleanly under a Kicker — unlike the kit's AbsoluteFill CodePanel. */
const CodeCard: React.FC<{
  filename: string;
  lines: Array<{ text: string; color?: string; focus?: string }>;
  width?: number;
  fontSize?: number;
}> = ({ filename, lines, width = 1180, fontSize = 24 }) => (
  <div
    style={{
      width,
      borderRadius: 12,
      overflow: "hidden",
      border: `1px solid ${COLORS.line}`,
      boxShadow: `0 28px 80px -36px #000, 0 0 30px -18px ${COLORS.cyan}`,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#0B0E13",
        borderBottom: `1px solid ${COLORS.line}`,
        padding: "10px 16px",
      }}
    >
      {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
        <span key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />
      ))}
      <span style={{ fontFamily: FONTS.mono, fontSize: 15, color: COLORS.dim, marginLeft: 8 }}>{filename}</span>
    </div>
    <pre
      style={{
        margin: 0,
        padding: "18px 24px",
        fontFamily: FONTS.mono,
        fontSize,
        lineHeight: 1.7,
        background: "#0E1117",
        whiteSpace: "pre-wrap",
      }}
    >
      {lines.map((l, i) => (
        <div
          key={i}
          style={{
            color: l.color ?? COLORS.text,
            background: l.focus ? `${l.focus}1A` : "transparent",
            boxShadow: l.focus ? `inset 2px 0 0 ${l.focus}` : "none",
            margin: "0 -24px",
            padding: "0 24px",
          }}
        >
          {l.text || " "}
        </div>
      ))}
    </pre>
  </div>
);

/* 01 — HOOK (the database vanished, my code was fine) */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const flash = interpolate(frame, [40, 70], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="incident · database outage" color={COLORS.red} />
          <Big>
            the database{" "}
            <span style={{ color: COLORS.red, opacity: 0.4 + flash * 0.6 }}>vanished</span>
            <br />
            my code was <span style={{ color: COLORS.green }}>fine</span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="red" />
    </AbsoluteFill>
  );
};

/* 02 — NOT YOU (platform incident · 522 · 57P03 · 1 GB Micro) */
export const Scene02NotYou: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.notyou.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="step 1 · is it even me?" color={COLORS.amber} />
        <div style={{ display: "flex", gap: 22, alignItems: "stretch", marginBottom: 22 }}>
          <Panel accent="red" title="app · login page" width={560}>
            <div style={{ display: "flex", alignItems: "center", gap: 24, padding: "10px 6px" }}>
              <div
                style={{
                  fontFamily: FONTS.display,
                  fontWeight: 700,
                  fontSize: 92,
                  color: COLORS.red,
                  lineHeight: 1,
                }}
              >
                522
              </div>
              <div
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 18,
                  color: COLORS.amber,
                  border: `1px solid ${COLORS.amber}55`,
                  borderRadius: 8,
                  padding: "12px 14px",
                  background: `${COLORS.amber}12`,
                  lineHeight: 1.4,
                }}
              >
                ⚠ investigating a<br />technical issue
              </div>
            </div>
          </Panel>
          <Panel accent="amber" title="database · compute tier" width={560}>
            <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center", padding: "10px 6px" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 46, color: COLORS.amber }}>Micro</div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 15, color: COLORS.dim, letterSpacing: "0.08em" }}>compute</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 46, color: COLORS.red }}>1 GB</div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 15, color: COLORS.dim, letterSpacing: "0.08em" }}>ram · smallest</div>
              </div>
            </div>
          </Panel>
        </div>
        <CodeCard
          filename="psql — connect attempt"
          width={1142}
          fontSize={24}
          lines={[
            { text: "$ psql $DATABASE_URL", color: COLORS.dim },
            { text: "FATAL: 57P03: the database system is not accepting connections", color: COLORS.red, focus: COLORS.red },
          ]}
        />
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={NOTYOU} accent="amber" />
  </AbsoluteFill>
);

/* 03 — STUCK (recovered @ 145ms, but every sync frozen at 0%) */
export const Scene03Stuck: React.FC = () => {
  const frame = useCurrentFrame();
  const pulse = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(frame / 7));
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.stuck.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="step 2 · it recovered" color={COLORS.cyan} />
          <Panel accent="green" width={760} style={{ marginBottom: 30 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, padding: "10px 6px" }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 30, color: COLORS.green }}>direct connect ✓</span>
              <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 44, color: COLORS.green }}>145 ms</span>
              <span style={{ fontFamily: FONTS.body, fontSize: 24, color: COLORS.dim }}>— healthy again</span>
            </div>
          </Panel>
          <div style={{ width: 760 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.amber }}>Processing…</span>
              <span style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.amber }}>0%</span>
            </div>
            <div
              style={{
                width: "100%",
                height: 34,
                border: `1px solid ${COLORS.amber}66`,
                borderRadius: 8,
                background: COLORS.panel2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: 14,
                  height: "100%",
                  background: COLORS.amber,
                  opacity: pulse,
                  boxShadow: `0 0 18px ${COLORS.amber}`,
                }}
              />
            </div>
            <div style={{ fontFamily: FONTS.body, fontSize: 24, color: COLORS.dim, marginTop: 18, textAlign: "center" }}>
              the database was fine — something <span style={{ color: COLORS.amber }}>else</span> was jammed
            </div>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={STUCK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 04 — ZOMBIE (a dead job still holds the lock — the coat-check ticket) */
export const Scene04Zombie: React.FC = () => {
  const frame = useCurrentFrame();
  const glow = 0.6 + 0.4 * (0.5 + 0.5 * Math.sin(frame / 9));
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.zombie.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the culprit · a zombie lock" color={COLORS.magenta} />
          <div style={{ display: "flex", alignItems: "center", gap: 34, marginBottom: 26 }}>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 26,
                color: COLORS.magenta,
                border: `1.5px solid ${COLORS.magenta}`,
                borderRadius: 12,
                padding: "18px 26px",
                background: `${COLORS.magenta}14`,
                boxShadow: `0 0 ${28 * glow}px -6px ${COLORS.magenta}`,
              }}
            >
              🎫 lock ticket — <span style={{ color: COLORS.text }}>“only I may run”</span>
            </div>
            <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 40, color: COLORS.dim }}>→</div>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 24,
                color: COLORS.dim,
                border: `1px dashed ${COLORS.line}`,
                borderRadius: 12,
                padding: "18px 26px",
                opacity: 0.5,
              }}
            >
              👤 owner — <span style={{ color: COLORS.red }}>gone</span>
            </div>
          </div>
          <CodeCard
            filename="sync_locks · the live row"
            width={1180}
            fontSize={23}
            lines={[
              { text: "job_id = 400b6b62   status = processing   held = 105 min", color: COLORS.magenta, focus: COLORS.magenta },
              { text: "// the process was killed before it could hand the ticket back", color: COLORS.dim },
            ]}
          />
          <div style={{ display: "flex", gap: 14, marginTop: 22 }}>
            {["queued sync", "queued sync", "queued sync"].map((c, i) => (
              <div
                key={i}
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 18,
                  color: COLORS.dim,
                  border: `1px solid ${COLORS.line}`,
                  borderRadius: 8,
                  padding: "8px 16px",
                  opacity: 0.55,
                }}
              >
                ⏳ {c} — blocked
              </div>
            ))}
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={ZOMBIE} accent="magenta" />
    </AbsoluteFill>
  );
};

/* 05 — DEAD ZONE (the 8→120 min gap · the centerpiece diagram) */
export const Scene05DeadZone: React.FC = () => {
  const frame = useCurrentFrame();
  const LX = 60;
  const trackW = 1480;
  const xOf = (m: number) => LX + (m / 120) * trackW;
  const x8 = xOf(8);
  const x105 = xOf(105);
  const x120 = xOf(120);
  const bandRight = interpolate(frame, [18, 78], [x8, x120], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const bandLabel = interpolate(frame, [64, 92], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const pinDrop = interpolate(frame, [84, 108], [-52, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const pinOpacity = interpolate(frame, [84, 104], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  const Tick: React.FC<{ x: number }> = ({ x }) => (
    <div style={{ position: "absolute", left: x - 1, top: 202, width: 2, height: 36, background: COLORS.dim }} />
  );

  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.deadzone.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="why nothing rescued it · the dead zone" color={COLORS.amber} />
          <div style={{ position: "relative", width: 1600, height: 360 }}>
            {/* axis baseline */}
            <div style={{ position: "absolute", left: LX, top: 220, width: trackW, height: 3, background: COLORS.line }} />
            {/* end labels */}
            <div style={{ position: "absolute", left: LX - 6, top: 250, fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>0 min</div>
            <div style={{ position: "absolute", left: x120 - 70, top: 250, fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim, width: 80, textAlign: "right" }}>120 min</div>
            {/* dead-zone band (grows) */}
            <div
              style={{
                position: "absolute",
                left: x8,
                top: 178,
                width: Math.max(0, bandRight - x8),
                height: 62,
                background: `${COLORS.red}26`,
                border: `1px solid ${COLORS.red}88`,
                borderRadius: 4,
              }}
            />
            {/* band label */}
            <div
              style={{
                position: "absolute",
                left: x8,
                top: 196,
                width: x120 - x8,
                textAlign: "center",
                fontFamily: FONTS.mono,
                fontSize: 24,
                letterSpacing: "0.14em",
                color: COLORS.amber,
                opacity: bandLabel,
              }}
            >
              DEAD ZONE · no on-demand rescue
            </div>
            {/* ticks */}
            <Tick x={LX} />
            <Tick x={x8} />
            <Tick x={x120} />
            {/* 8-min tick label */}
            <div style={{ position: "absolute", left: x8 - 8, top: 250, width: 360, fontFamily: FONTS.mono, fontSize: 17, color: COLORS.text }}>
              <span style={{ color: COLORS.amber }}>8 min</span> — boot recovery
              <div style={{ color: COLORS.dim, fontSize: 15 }}>didn’t fire (nothing restarted)</div>
            </div>
            {/* 120-min tick label */}
            <div style={{ position: "absolute", left: x120 - 320, top: 292, width: 320, textAlign: "right", fontFamily: FONTS.mono, fontSize: 17, color: COLORS.text }}>
              <span style={{ color: COLORS.amber }}>120 min</span> — age sweep
              <div style={{ color: COLORS.dim, fontSize: 15 }}>too young — only 105 min old</div>
            </div>
            {/* you-are-here pin @105 */}
            <div style={{ position: "absolute", left: x105 - 1, top: 150 + pinDrop, width: 2, height: 92, background: COLORS.cyan, opacity: pinOpacity, boxShadow: `0 0 12px ${COLORS.cyan}` }} />
            <div
              style={{
                position: "absolute",
                left: x105 - 372,
                top: 150 + pinDrop,
                width: 356,
                textAlign: "right",
                fontFamily: FONTS.mono,
                fontSize: 20,
                color: COLORS.cyan,
                opacity: pinOpacity,
              }}
            >
              ⟵ you are here · 105 min
            </div>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={DEADZONE} accent="amber" />
    </AbsoluteFill>
  );
};

/* 06 — THE FIX (acquire-time heartbeat lock-steal) */
export const Scene06Fix: React.FC = () => {
  const frame = useCurrentFrame();
  const pop = interpolate(frame, [70, 100], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.fix.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the fix · recover on demand" color={COLORS.green} />
          <CodeCard
            filename="sync-lock.ts · stealLockIfHolderDead()"
            width={1280}
            fontSize={24}
            lines={[
              { text: "// a blocked sync asks: is the holder actually dead?", color: COLORS.dim },
              { text: "holder heartbeat silent > 8 min  →  STEAL the lock, proceed", color: COLORS.green, focus: COLORS.green },
              { text: "              otherwise (alive)  →  wait", color: COLORS.dim },
            ]}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 30, marginTop: 26 }}>
            <div style={{ fontFamily: FONTS.body, fontSize: 22, color: COLORS.dim, maxWidth: 620 }}>
              🧥 the attendant reclaims the coat and hands it to the guest waiting at the counter
            </div>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 22,
                color: COLORS.green,
                border: `1.5px solid ${COLORS.green}`,
                borderRadius: 10,
                padding: "12px 20px",
                background: `${COLORS.green}14`,
                opacity: pop,
                transform: `scale(${0.9 + pop * 0.1})`,
                boxShadow: `0 0 26px -8px ${COLORS.green}`,
              }}
            >
              self-heals in &lt; 8 min
            </div>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={FIX} accent="green" />
    </AbsoluteFill>
  );
};

/* 07 — LANDMINE (the fail-open trap the review caught → fail closed) */
export const Scene07Landmine: React.FC = () => {
  const frame = useCurrentFrame();
  const resolve = interpolate(frame, [90, 130], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const failColor = `rgba(${Math.round(255 - 212 * resolve)}, ${Math.round(77 + 178 * resolve)}, ${Math.round(77 + 59 * resolve)}, 1)`;
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.landmine.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the bug that almost shipped" color={COLORS.red} />
          <div style={{ display: "flex", gap: 30, alignItems: "stretch", marginBottom: 30 }}>
            <Panel accent="red" title="v1 · fail OPEN (the trap)" width={620}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 6, fontFamily: FONTS.body, fontSize: 22, color: COLORS.text }}>
                <div>a sync that JUST started — no heartbeat yet</div>
                <div style={{ color: COLORS.dim }}>read empty timestamp → <span style={{ color: COLORS.red }}>assume dead</span></div>
                <div>STEAL the lock from a <span style={{ color: COLORS.red }}>living</span> sync</div>
                <div style={{ color: COLORS.red, fontWeight: 600 }}>💥 two syncs at once → OOM — the crash the lock prevents</div>
              </div>
            </Panel>
            <Panel accent="green" title="v2 · fail CLOSED (shipped)" width={620}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 6, fontFamily: FONTS.body, fontSize: 22, color: COLORS.text }}>
                <div>no heartbeat signal → <span style={{ color: COLORS.amber }}>unsure</span> if dead</div>
                <div>when unsure → <span style={{ color: COLORS.green }}>DON’T steal</span></div>
                <div style={{ color: COLORS.dim }}>wait for a real timeout instead</div>
                <div style={{ color: COLORS.green, fontWeight: 600 }}>✓ an adversarial review caught it first</div>
              </div>
            </Panel>
          </div>
          <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 64, letterSpacing: "0.04em", color: failColor }}>
            FAIL CLOSED
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={LANDMINE} accent="red" />
    </AbsoluteFill>
  );
};

/* 08 — LAYERS (the five recovery layers) */
export const Scene08Layers: React.FC = () => {
  const layers = [
    { n: "1", t: "finally-release", d: "instant · the happy path", accent: COLORS.violet, isNew: false },
    { n: "2", t: "boot recovery", d: "8 min · on restart", accent: COLORS.violet, isNew: false },
    { n: "3", t: "stuck-job sweep", d: "120 min · age timer", accent: COLORS.violet, isNew: false },
    { n: "4", t: "stale-lock GC", d: "4 hour · janitor", accent: COLORS.violet, isNew: false },
    { n: "5", t: "acquire-time steal", d: "on demand · closes the dead zone", accent: COLORS.green, isNew: true },
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.layers.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the full picture · five layers" color={COLORS.cyan} />
          <div style={{ display: "flex", flexDirection: "column", gap: 12, width: 1180 }}>
            {layers.map((l) => (
              <div
                key={l.n}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 22,
                  border: `1px solid ${l.isNew ? l.accent : COLORS.line}`,
                  borderLeft: `5px solid ${l.accent}`,
                  borderRadius: 8,
                  background: l.isNew ? `${COLORS.green}12` : COLORS.panel,
                  padding: "14px 24px",
                }}
              >
                <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 34, color: l.accent, minWidth: 40 }}>{l.n}</div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.text, minWidth: 340 }}>{l.t}</div>
                <div style={{ fontFamily: FONTS.body, fontSize: 21, color: COLORS.dim }}>{l.d}</div>
                {l.isNew && (
                  <div style={{ marginLeft: "auto", fontFamily: FONTS.mono, fontSize: 16, letterSpacing: "0.16em", color: COLORS.green }}>NEW</div>
                )}
              </div>
            ))}
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={LAYERS} accent="violet" />
    </AbsoluteFill>
  );
};

/* 09 — OUTRO (the lesson) */
export const Scene09Outro: React.FC = () => {
  const frame = useCurrentFrame();
  const fade = interpolate(frame, [0, 24], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <div style={{ opacity: fade }}>
            <Kicker text="the lesson" color={COLORS.green} />
          </div>
          <Big size={66}>
            recover <span style={{ color: COLORS.green }}>on demand</span>, not on a timer
            <br />
            a lock needs a <span style={{ color: COLORS.cyan }}>heartbeat</span>
          </Big>
          <div style={{ fontFamily: FONTS.mono, fontSize: 26, color: COLORS.dim, marginTop: 30 }}>
            and when in doubt — <span style={{ color: COLORS.green }}>fail closed</span>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={OUTRO} accent="green" />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
